import React, { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress, SxProps, Theme } from '@mui/material';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Controller } from 'react-hook-form';
import i18next from 'i18next';
import { SPACES_LIST_PAGED_HIERARCHICAL_QUERY, SPACE_VIEW_QUERY } from 'pages/settings/gql';
import { SpaceHierarchicalFragment } from '__generated__/graphql';

interface PaginatedSpaceSelectProps {
  onSelect: (spaceId: number, spaceName: string) => void;
  excludeSpaceIds?: (number | null | undefined)[];
  placeholder?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

const PAGE_SIZE = 20;

/**
 * Autocomplete component for selecting spaces with pagination and search.
 * Supports debounced search and infinite scroll for large space lists.
 */
export const PaginatedSpaceSelect: React.FC<PaginatedSpaceSelectProps> = ({
  onSelect,
  excludeSpaceIds = [],
  placeholder,
  disabled = false,
  sx,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState<SpaceHierarchicalFragment[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const [fetchSpaces, { loading }] = useLazyQuery(SPACES_LIST_PAGED_HIERARCHICAL_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const newSpaces = data?.listSpacesPagedHierarchical?.spaces || [];
      const totalCount = data?.listSpacesPagedHierarchical?.totalCount || 0;

      if (page === 0) {
        setOptions(newSpaces);
      } else {
        setOptions(prev => [...prev, ...newSpaces]);
      }

      const loadedCount = (page + 1) * PAGE_SIZE;
      setHasMore(loadedCount < totalCount);
    },
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
      setPage(0);
      setOptions([]);
      setHasMore(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch when debounced search changes or page changes
  useEffect(() => {
    if (isOpen) {
      fetchSpaces({
        variables: {
          skip: page * PAGE_SIZE,
          take: PAGE_SIZE,
          search: debouncedSearch.trim().length > 0 ? debouncedSearch.trim() : null,
        },
      });
    }
  }, [debouncedSearch, page, isOpen, fetchSpaces]);

  const handleScroll = useCallback((event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50 &&
      hasMore &&
      !loading
    ) {
      setPage(prev => prev + 1);
    }
  }, [hasMore, loading]);

  const handleOpen = () => {
    setIsOpen(true);
    setPage(0);
    setOptions([]);
    setHasMore(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setInputValue('');
  };

  // Filter out excluded space IDs
  const filteredOptions = options.filter(
    opt => !excludeSpaceIds.includes(opt.space.id)
  );

  return (
    <Autocomplete
      sx={sx}
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      disabled={disabled}
      options={filteredOptions}
      loading={loading}
      filterOptions={(x) => x} // Disable client-side filtering, server handles it
      getOptionLabel={(option) => option.displayName || option.space.name}
      isOptionEqualToValue={(option, value) => option.space.id === value.space.id}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={(_, value) => {
        if (value) {
          onSelect(value.space.id, value.space.name);
          setInputValue('');
          setIsOpen(false);
        }
      }}
      ListboxProps={{
        onScroll: handleScroll,
        style: { maxHeight: 300 },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder || (i18next.t('user-membership-add') as string)}
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.space.id}>
          {option.displayName || option.space.name}
        </li>
      )}
      noOptionsText={loading ? i18next.t('generic-loading') : i18next.t('generic-no-results')}
    />
  );
};

// react-hook-form integrated version
interface FormInputPaginatedSpaceSelectProps {
  name: string;
  control: any;
  label?: string;
  excludeSpaceIds?: (number | null | undefined)[];
  disabled?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
  sx?: SxProps<Theme>;
}

interface SpaceOption {
  id: number;
  name: string;
  displayName?: string;
}

/**
 * Form input wrapper for PaginatedSpaceSelect integrated with react-hook-form.
 * Provides space selection with pagination for form contexts.
 */
export const FormInputPaginatedSpaceSelect: React.FC<FormInputPaginatedSpaceSelectProps> = ({
  name,
  control,
  label,
  excludeSpaceIds = [],
  disabled = false,
  allowEmpty = true,
  emptyLabel,
  sx,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState<SpaceHierarchicalFragment[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<SpaceOption | null>(null);

  const [fetchSpaces, { loading }] = useLazyQuery(SPACES_LIST_PAGED_HIERARCHICAL_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const newSpaces = data?.listSpacesPagedHierarchical?.spaces || [];
      const totalCount = data?.listSpacesPagedHierarchical?.totalCount || 0;

      if (page === 0) {
        setOptions(newSpaces);
      } else {
        setOptions(prev => [...prev, ...newSpaces]);
      }

      const loadedCount = (page + 1) * PAGE_SIZE;
      setHasMore(loadedCount < totalCount);
    },
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
      setPage(0);
      setOptions([]);
      setHasMore(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch when debounced search changes or page changes
  useEffect(() => {
    if (isOpen) {
      fetchSpaces({
        variables: {
          skip: page * PAGE_SIZE,
          take: PAGE_SIZE,
          search: debouncedSearch.trim().length > 0 ? debouncedSearch.trim() : null,
        },
      });
    }
  }, [debouncedSearch, page, isOpen, fetchSpaces]);

  const handleScroll = useCallback((event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50 &&
      hasMore &&
      !loading
    ) {
      setPage(prev => prev + 1);
    }
  }, [hasMore, loading]);

  const handleOpen = () => {
    setIsOpen(true);
    setPage(0);
    setOptions([]);
    setHasMore(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    // Don't clear inputValue here to preserve selected display
  };

  // Filter out excluded space IDs
  const filteredOptions = options.filter(
    opt => !excludeSpaceIds.includes(opt.space.id)
  );

  // Build options list with empty option if allowed
  const allOptions: SpaceOption[] = [
    ...(allowEmpty ? [{ id: 0, name: emptyLabel || (i18next.t('generic-selection-empty') as string), displayName: emptyLabel || (i18next.t('generic-selection-empty') as string) }] : []),
    ...filteredOptions.map(opt => ({
      id: opt.space.id,
      name: opt.space.name,
      displayName: opt.displayName || opt.space.name,
    })),
  ];

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        // Load space data if we have a value but no selected option
        const { data: spaceData } = useQuery(SPACE_VIEW_QUERY, {
          variables: { id: value },
          skip: !value || value === 0 || !!(selectedOption && selectedOption.id === value),
        });

        // Update selected option when space data is loaded
        useEffect(() => {
          if (spaceData?.viewSpace && (!selectedOption || selectedOption.id !== value)) {
            setSelectedOption({
              id: spaceData.viewSpace.id,
              name: spaceData.viewSpace.name,
              displayName: spaceData.viewSpace.name,
            });
          }
        }, [spaceData, value]);

        // Handle empty value
        useEffect(() => {
          if ((!value || value === 0) && selectedOption) {
            setSelectedOption(null);
          }
        }, [value]);

        const currentValue = value && value !== 0
          ? (selectedOption || { id: value, name: `Space ${value}`, displayName: `Space ${value}` })
          : (allowEmpty ? { id: 0, name: emptyLabel || (i18next.t('generic-selection-empty') as string), displayName: emptyLabel || (i18next.t('generic-selection-empty') as string) } : null);

        return (
          <Autocomplete
            sx={sx}
            open={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            disabled={disabled}
            options={allOptions}
            loading={loading}
            value={currentValue}
            filterOptions={(x) => x}
            getOptionLabel={(option) => option?.displayName || option?.name || ''}
            isOptionEqualToValue={(option, val) => !!val && option.id === val.id}
            inputValue={isOpen ? inputValue : (currentValue?.displayName || currentValue?.name || '')}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'input') {
                setInputValue(newInputValue);
              }
            }}
            onChange={(_, newValue) => {
              if (newValue) {
                onChange(newValue.id || null);
                setSelectedOption(newValue.id ? newValue : null);
              } else if (allowEmpty) {
                onChange(null);
                setSelectedOption(null);
              }
            }}
            ListboxProps={{
              onScroll: handleScroll,
              style: { maxHeight: 300 },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                error={!!error}
                helperText={error?.message}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.id === 0 ? <em>{option.displayName}</em> : option.displayName}
              </li>
            )}
            noOptionsText={loading ? i18next.t('generic-loading') : i18next.t('generic-no-results')}
          />
        );
      }}
    />
  );
};
